import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core';
import { states } from '../../../lib/states';
import {
  debounceTime,
  distinctUntilChanged,
  takeUntil,
  startWith,
  map,
} from 'rxjs/operators';
import { UniversalSearchService } from '../../../universal-search/services/universal-search.service';
import { DateTime } from 'luxon';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaskService } from '../../../services/mask.service';
import { CaseManagementService } from '../../../case-management/case-management.service';
import { PostTradeService } from '../../../post-trade/post-trade.service';
import { CustomFiltersService } from '../../../services/custom-filters.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { RbGridService } from '../../rb-grid/rb-grid.service';
import { Subject, Observable } from 'rxjs';
import { DashboardService } from '../../../dashboard/dashboard.service';
import { RightBridgeApiService } from '../../../services/right-bridge-api.service';
import { BookManagementService } from '../../../book-management/book-management.service';
import { HashResult } from '../../../models/post-trade-models';
import { UnitFilterComponent } from './unit-filter/unit-filter.component';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MaskitoDirective } from '@maskito/angular';
import { RepCodeFilterComponent } from './repCode-filter/repCode-filter.component';
import { UserFilterComponent } from './user-filter/user-filter.component';
import { NpnFilterComponent } from './npn-filter/npn-filter.component';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MultiselectFilterComponent } from './multiselect-filter/multiselect-filter.component';
import { ChipFilterComponent } from './chip-filter/chip-filter.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-grid-filter-fields',
  templateUrl: './grid-filter-fields.component.html',
  styleUrls: ['./grid-filter-fields.component.scss'],
  imports: [
    UnitFilterComponent,
    CommonModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MaskitoDirective,
    RepCodeFilterComponent,
    UserFilterComponent,
    NpnFilterComponent,
    MatIconModule,
    MatSelectModule,
    MatAutocompleteModule,
    MultiselectFilterComponent,
    ChipFilterComponent,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatMenuModule,
    MatTooltipModule,
    MatButtonModule,
  ],
  providers: [],
})
export class GridFilterFieldsComponent implements OnInit, OnDestroy {
  private gridSrvc = inject(RbGridService);
  private ss = inject(SessionStorageService);
  private dbs = inject(DashboardService);
  private cms = inject(CaseManagementService);
  private ptSvc = inject(PostTradeService);
  usServ = inject(UniversalSearchService);
  cstmFltrSrvc = inject(CustomFiltersService);
  private mask = inject(MaskService);
  private rbs = inject(RightBridgeApiService);
  private bmSvc = inject(BookManagementService);

  @Input() field;
  @Input() filterForm;
  @Input() groupKey;
  @Input() gridName: string;
  @Input() currentFilters;
  @Output() textChange = new EventEmitter();
  @Output() valueRangeChange = new EventEmitter();
  @Output() dateChange = new EventEmitter();
  @Output() relatedDateChange = new EventEmitter();
  @Output() selectionChange = new EventEmitter();
  @Output() clearFilter = new EventEmitter();
  @Output() additionalData = new EventEmitter();
  @ViewChild('pickerField') pickerField;
  @ViewChild('startDatePicker') startDatePicker;
  @ViewChild('endDatePicker') endDatePicker;

  floatingRangeOptions = [
    { value: '7 days', display: 'Last 7 Days' },
    { value: '30 days', display: 'Last 30 Days' },
    { value: '90 days', display: 'Last 90 Days' },
    { value: 'mtd', display: 'Month-to-Date' },
    { value: 'ytd', display: 'Year-to-Date' },
    { value: 'custom', display: 'Custom Range' },
  ];
  start = new FormControl<Date | null>(null);
  end = new FormControl<Date | null>(null);
  relatedStart = new FormControl<Date | null>(null);
  relatedEnd = new FormControl<Date | null>(null);
  dollarMask = this.mask.dollarMaskSpecs();
  states = states;
  unsubscribe = new Subject();
  typeaheadControl = new FormControl<any>('');
  tyepaheadOptions = [];
  filteredOptions: Observable<any>;
  minField = new FormControl<string | null>(null);
  maxField = new FormControl<string | null>(null);
  rangeEvent;
  hashedControl = new FormControl<any>('');
  valueHashed = false;
  hashEvent;
  solutions = [];
  reportTypes = [];
  reportsLoading = true;
  reportSolution;
  presetRangeControl = new FormControl<any>('');
  globals = this.ss.get('globals');
  dropdownLoading: boolean;
  dropdownNoResults: boolean;
  dropdownRepCodes = [];
  bridgeIdOptions: any;
  minDate;
  showBridgeIdSelect: boolean = true;

  ngOnInit(): void {
    if (
      this.gridName === 'BookManagement' ||
      this.gridName === 'CarrierPolicyDetails'
    ) {
      this.floatingRangeOptions.splice(5, 0, {
        value: '12mtd',
        display: '12 Months (to Date)',
      });
    }

    if (
      this.field.controlType === 'range' ||
      this.field.controlType === 'relatedDate' ||
      this.field.controlType === 'presetRange'
    ) {
      this.presetRangeControl =
        this.filterForm.controls[this.groupKey].controls[this.field.varName];
      this.cstmFltrSrvc.updatePresets$
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: field => {
            if (field && field['field'] === this.field.varName) {
              if (field && field['customRange']) {
                if (field['value'] === 'relatedSub') {
                  this.relatedStart.setValue(
                    DateTime.fromISO(field['customRange']['start'], {
                      zone: 'utc',
                    })
                      .plus({ days: 1 })
                      .startOf('day')
                      .toISO()
                  );
                  this.relatedEnd.setValue(
                    new Date(field['customRange']['end'])
                  );
                } else {
                  if (field && this.field.controlType === 'presetRange') {
                    this.presetRangeControl.setValue('custom');
                  }
                  this.start.setValue(
                    DateTime.fromISO(field['customRange']['start'], {
                      zone: 'utc',
                    })
                      .plus({ days: 1 })
                      .startOf('day')
                      .toISO()
                  );
                  this.end.setValue(new Date(field['customRange']['end']));
                }
              } else if (
                field &&
                this.field.controlType === 'presetRange' &&
                (field['value'].includes('days') ||
                  field['value'].includes('td'))
              ) {
                this.presetRangeControl.setValue(field['value']);
              } else if (
                field &&
                this.field.controlType === 'presetRange' &&
                (field['operator'] === 'start' || field['operator'] === 'end')
              ) {
                this[field['operator']].setValue(field['value']);
                setTimeout(() => {
                  if (field['presetSelection']) {
                    this.presetRangeControl.setValue(field['presetSelection']);
                  } else if (this.start.value && this.end.value) {
                    this.presetRangeControl.setValue(
                      this.cstmFltrSrvc.calculatePresetSelection(
                        this.start.value,
                        this.end.value
                      )
                    );
                  }
                });
              } else if (
                field &&
                this.field.controlType === 'range' &&
                (field['operator'] === 'start' || field['operator'] === 'end')
              ) {
                this[field['operator']].setValue(field['value']);
              } else if (
                field &&
                field?.['field'] === this.field.varName &&
                this.field.controlType === 'relatedDate' &&
                (field['operator'] === 'start' || field['operator'] === 'end')
              ) {
                this[field['operator']].setValue(field['value']);
              } else {
                this.relatedStart.setValue(null);
                this.relatedEnd.setValue(null);
                this.start.setValue(null);
                this.end.setValue(null);
              }
            }
          },
          error: error => alert(error),
        });
      if (this.gridName === 'CarrierPolicyDetails') {
        this.minDate = DateTime.now()
          .minus({ years: 1 })
          .startOf('month')
          .toUTC()
          .toISO();
      }
    }
    if (
      this.field.controlType === 'valueRange' ||
      this.field.controlType === 'currencyRange'
    ) {
      this.cstmFltrSrvc.updateValueRange$
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: field => {
            if (field?.['field'] === this.field.varName) {
              if (field && field['operator'] === 'gte') {
                this.minField.setValue(field['value']);
              }
              if (field && field['operator'] === 'lte') {
                this.maxField.setValue(field['value']);
              }
            }
          },
          error: error => console.error('Error: ', error),
        });
    }
    if (this.field.controlType === 'dropdown') {
      this.typeaheadControl =
        this.filterForm.controls[this.groupKey].controls[this.field.varName];
      this.cstmFltrSrvc.updateDropdowns$
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: field => {
            if (field && field['values']) {
              if (
                field['values']?.['field'] === this.field.varName &&
                (typeof field['values']?.['value'] === 'number' ||
                  field['values']?.['value'])
              ) {
                this.typeaheadControl.setValue(field['values']?.['value']);
              }
              if (
                field['values']?.['value']['max'] ||
                field['values']?.['value']['min']
              ) {
                this.minField.setValue(field['values']?.['value']['min']);
                this.maxField.setValue(field['values']?.['value']['max']);
              }
              if (
                field['values']?.['value']['max'] ||
                field['values']?.['value']['min']
              ) {
                this.minField.setValue(field['values']?.['value']['min']);
                this.maxField.setValue(field['values']?.['value']['max']);
              }
            } else {
              if (
                !field['varName'] ||
                (!field['values'] && field['varName'] === this.field.varName)
              ) {
                this.typeaheadControl.setValue(null);
                this.minField.setValue(null);
                this.maxField.setValue(null);
              }
            }
          },
          error: error => alert(error),
        });
      this.field.options.forEach(option => {
        this.tyepaheadOptions.push(option);
      });
      this.filteredOptions = this.typeaheadControl.valueChanges.pipe(
        startWith(''),
        map(value => {
          if (typeof value !== 'string' && value !== null) {
            this.filterForm.controls[this.groupKey].controls[
              this.field.varName
            ].setValue(value.value);
          }
          const name = typeof value === 'string' ? value : value?.label;
          return name
            ? this._filter(name as string)
            : this.tyepaheadOptions.slice();
        })
      );
      const defaultValue = this.tyepaheadOptions.find(
        option =>
          option.value ===
          this.filterForm.controls[this.groupKey].controls[this.field.varName]
            .value
      );
      this.typeaheadControl.setValue(
        typeof defaultValue === 'string' ? defaultValue : defaultValue?.value
      );
    }

    if (this.field.controlType === 'solutionselect') {
      this.solutions = this.gridSrvc.getSolutionOptions(this.gridName);
      this.field.options = [...this.solutions];
    }

    if (this.field.controlType === 'repreportselect') {
      this.filterForm.controls[this.groupKey].controls[
        this.field.varName
      ].disable();
      this.reportsLoading = true;
      this.dbs.getDashboardGlobals().subscribe(resp => {
        if (resp['RepBehaviorReport']) {
          resp['RepBehaviorReport'].map(report => {
            this.reportTypes.push({
              label: report.ReportName,
              value: report.ReportName,
              module: report.Module,
            });
          });
          this.additionalData.emit({
            repBehaviorReport: resp['RepBehaviorReport'],
          });
        }
        this.reportsLoading = false;
      });

      this.cstmFltrSrvc.updateSolution$
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: solutionVal => {
            this.reportSolution = solutionVal;
            // this.cstmFltrSrvc.updateRepReportSelection(null);
            if (!solutionVal) {
              this.filterForm.controls[this.groupKey].controls[
                this.field.varName
              ].disable();
              this.filterForm.controls[this.groupKey].controls[
                this.field.varName
              ].setValue(null);
            } else {
              this.filterForm.controls[this.groupKey].controls[
                this.field.varName
              ].enable();
              this.filterForm.controls[this.groupKey].controls[
                this.field.varName
              ].setValue(solutionVal);
            }
          },
          error: error => alert(error),
        });
      this.cstmFltrSrvc.updateRepReport$
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: repReport => {
            if (!repReport) {
              this.filterForm.controls[this.groupKey].controls[
                this.field.varName
              ].setValue(null);
            } else {
              this.filterForm.controls[this.groupKey].controls[
                this.field.varName
              ].setValue(repReport);
            }
          },
          error: error => alert(error),
        });
    }

    if (this.field.controlType === 'hashedInput') {
      this.hashedControl =
        this.filterForm.controls[this.groupKey].controls[this.field.varName];
      this.cstmFltrSrvc.updateDropdowns$
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: field => {
            if (
              field &&
              field['values']?.['field'] === this.field.varName &&
              (typeof field['values']?.['value'] === 'number' ||
                field['values']?.['value'])
            ) {
              this.valueHashed = true;
              this.hashedControl.setValue(field['values']?.['value']);
            }
          },
          error: error => alert(error),
        });
    }

    if (
      this.field.controlType === 'currencyRange' ||
      this.field.controlType === 'valueRange'
    ) {
      this.minField.valueChanges
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe(value => {
          const valueRange = {
            min: value,
          };
          if (this.maxField.value) {
            valueRange['max'] = this.maxField.value;
          }

          this.filterForm.controls[this.groupKey].controls[
            this.field.varName
          ].setValue(valueRange);
          if (!this.rangeEvent) {
            this.rangeEvent = new InputEvent(null);
          }
          this.rangeEvent['valueRange'] = valueRange;
          this.valueRangeChange.emit(this.rangeEvent);
        });
      this.maxField.valueChanges
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe(value => {
          const valueRange = {
            max: value,
          };
          if (this.minField.value) {
            valueRange['min'] = this.minField.value;
          }
          this.filterForm.controls[this.groupKey].controls[
            this.field.varName
          ].setValue(valueRange);
          if (!this.rangeEvent) {
            this.rangeEvent = new InputEvent(null);
          }
          this.rangeEvent['valueRange'] = valueRange;
          this.valueRangeChange.emit(this.rangeEvent);
        });
    }

    if (this.field.controlType === 'bridgeidselect') {
      this.getBridgeOptions();
    }

    if (this.field.conditionalField) {
      this.filterForm.controls[this.groupKey].controls[
        this.field.varName
      ].disable();
      this.cstmFltrSrvc.updateConditionalFilter$
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: passedFilter => {
            if (
              passedFilter['varName'] === this.field.conditionalField &&
              (passedFilter['values'] === this.field.conditionalValue ||
                passedFilter['values'].includes(this.field.conditionalValue))
            ) {
              this.filterForm.controls[this.groupKey].controls[
                this.field.varName
              ].enable();
            } else if (
              passedFilter['varName'] === this.field.conditionalField &&
              (passedFilter['values'] !== this.field.conditionalValue ||
                !passedFilter['values'].includes(this.field.conditionalValue))
            ) {
              const resetEvent = new PointerEvent('pointerdown');
              this.resetFilter(resetEvent);
              this.filterForm.controls[this.groupKey].controls[
                this.field.varName
              ].reset({ value: null, disabled: true });
            }
          },
          error: error => console.error('Error: ', error),
        });
    }
  }

  private _filter(name: string) {
    const filterValue = name.toLowerCase();

    return this.field.options.filter(option =>
      typeof option.value === 'string'
        ? option.value.toLowerCase().includes(filterValue)
        : null
    );
  }

  displayFn() {
    return (
      this.field.options.find(
        option => option.value === this.typeaheadControl.value
      )?.display || this.typeaheadControl.value
    );
  }

  ngOnDestroy() {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  changeText(ev) {
    this.filterForm.controls[this.groupKey].controls[
      this.field.varName
    ].setValue(ev.target.value);
    this.textChange.emit(ev);
  }

  changeValueRange(ev, fieldRange) {
    this[fieldRange].setValue(ev.target.value);
    this.rangeEvent = ev;
  }

  changeSelection(ev) {
    if (this.field.controlType === 'dropdown') {
      if (
        this.field.varName === 'SourceScore' ||
        this.field.varName === 'DestinationScore' ||
        this.field.varName === 'LowestScore'
      ) {
        this.typeaheadControl.setValue(
          ev.option.value.charAt(0).toUpperCase() + ev.option.value.slice(1)
        );
        this.filterForm.controls[this.groupKey].controls[
          this.field.varName
        ].setValue(ev.option.value);
      } else {
        this.typeaheadControl.setValue(ev.option.getLabel());
        this.filterForm.controls[this.groupKey].controls[
          this.field.varName
        ].setValue(ev.option.value);
      }
    }
    if (this.field.controlType === 'solutionselect') {
      this.cstmFltrSrvc.updateSolutionSelection(ev.value);
      this.cstmFltrSrvc.updateRepReportSelection(null);
    }
    if (this.field.controlType === 'repreportselect') {
      this.cstmFltrSrvc.updateRepReportSelection(ev.value);
    }
    if (this.field.conditionalParent) {
      this.cstmFltrSrvc.updateConditionalValue(
        ev.option.value,
        this.field.varName
      );
    }
    this.selectionChange.emit(ev);
  }

  changeDate(ev) {
    if (this.field.controlType === 'presetRange') {
      // Change date range select to custom
      this.filterForm.controls[this.groupKey].controls[
        this.field.varName
      ].setValue('custom');
    }

    if (this.field.controlType === 'relatedDate') {
      // Clear the related dates
      this.relatedStart.setValue(null);
      this.relatedEnd.setValue(null);
      this.filterForm.controls[this.groupKey].controls[
        this.field.varName
      ].setValue('relatedMain');

      if (
        this.filterForm.controls[this.groupKey].controls[
          this.field.relatedVarName
        ]
      ) {
        this.filterForm.controls[this.groupKey].controls[
          this.field.relatedVarName
        ].setValue(null);
      }
    }

    if (this.minDate && ev.targetElement.className.includes('start-date')) {
      const dateMin = DateTime.fromISO(this.minDate, { zone: 'utc' });
      const setStart = DateTime.fromJSDate(new Date(ev.value))
        .startOf('day')
        .toUTC();

      if (setStart.diff(dateMin).values.milliseconds < 0) {
        ev.value = DateTime.fromISO(this.minDate, { zone: 'utc' }).toJSDate();
        this.start.setValue(ev.value);
      }
    }

    this.dateChange.emit(ev);
  }

  clearDateField(ev) {
    this.start.setValue(null);
    this.end.setValue(null);
    this.filterForm.controls[this.groupKey].controls[
      this.field.varName
    ].setValue(null);

    this.dateChange.emit({
      target: this.startDatePicker,
      targetElement: this.startDatePicker.nativeElement,
      value: null,
    });
    this.dateChange.emit({
      target: this.endDatePicker,
      targetElement: this.endDatePicker.nativeElement,
      value: null,
    });

    this.clearFilter.emit(ev);
  }

  changeRelatedDate(ev) {
    this.relatedDateChange.emit(ev);
    if (
      !this.filterForm.controls[this.groupKey].controls[
        this.field.relatedVarName
      ]
    ) {
      this.filterForm.controls[this.groupKey].addControl(
        this.field.relatedVarName,
        new FormControl()
      );
    }
    this.filterForm.controls[this.groupKey].controls[
      this.field.relatedVarName
    ].setValue('relatedSub');

    this.filterForm.controls[this.groupKey].controls[
      this.field.varName
    ].setValue('');
    // Clear the related dates
    this.start.setValue(null);
    this.end.setValue(null);
  }

  resetFilter(ev) {
    if (this.typeaheadControl['value']) {
      this.typeaheadControl.setValue('');
    }
    if (this.field.controlType === 'solutionselect') {
      this.cstmFltrSrvc.updateSolutionSelection(null);
    }
    this.clearFilter.emit(ev);
  }

  setHashEvent(ev) {
    this.valueHashed = false;
    this.hashEvent = ev;
  }

  hashInput(ev) {
    if (!ev.target.value) {
      this.hashedControl.setValue('');
      this.changeText(this.hashEvent);
    } else if (ev.target.value && !this.valueHashed) {
      this.cstmFltrSrvc.hashingStatus.set(true);
      this.ptSvc
        .getHashString(ev.target.value)
        .subscribe((resp: HashResult) => {
          this.valueHashed = true;
          if (resp.resultString) {
            this.cstmFltrSrvc.hashingStatus.set(false);
            this.filterForm.controls[this.groupKey].controls[
              this.field.varName
            ].setValue(resp.resultString);
            this.hashedControl.setValue(resp.resultString);
            this.hashEvent.target.value = resp.resultString;
            this.changeText(this.hashEvent);
          }
        });
    }
  }

  getDropdownOptions() {
    return this.field.fullField.externalFilter['options'] || [];
  }

  showClearButton(fieldName) {
    if (this.filterForm.controls[this.groupKey].controls[fieldName].value) {
      return true;
    }
    return false;
  }

  rangeSelection(ev) {
    const { value } = ev;
    if (value === 'custom') {
      setTimeout(() => {
        this.pickerField.open();
      });
    } else if (value === 'mtd') {
      // Set the date picker with the selected times
      const calculatedStartDate = DateTime.now()
        .startOf('month')
        .toLocaleString();
      this.start.setValue(new Date(calculatedStartDate));
      this.end.setValue(new Date());
      // Fire dateChange method to update the filters
      this.dateChange.emit({
        target: this.startDatePicker,
        targetElement: this.startDatePicker.nativeElement,
        value: new Date(calculatedStartDate),
      });
      this.dateChange.emit({
        target: this.endDatePicker,
        targetElement: this.endDatePicker.nativeElement,
        value: new Date(),
        presetSelection: 'mtd',
      });
    } else if (value === 'ytd') {
      // Set the date picker with the selected times
      const calculatedStartDate = DateTime.now()
        .startOf('year')
        .toLocaleString();
      this.start.setValue(new Date(calculatedStartDate));
      this.end.setValue(new Date());
      // Fire dateChange method to update the filters
      this.dateChange.emit({
        target: this.startDatePicker,
        targetElement: this.startDatePicker.nativeElement,
        value: new Date(calculatedStartDate),
      });
      this.dateChange.emit({
        target: this.endDatePicker,
        targetElement: this.endDatePicker.nativeElement,
        value: new Date(),
        presetSelection: 'ytd',
      });
    } else if (value === '12mtd') {
      // Set the date picker with the selected times
      const calculatedStartDate = DateTime.now()
        .minus({ years: 1 })
        .startOf('month')
        .toLocaleString();
      this.start.setValue(new Date(calculatedStartDate));
      this.end.setValue(new Date());
      // Fire dateChange method to update the filters
      this.dateChange.emit({
        target: this.startDatePicker,
        targetElement: this.startDatePicker.nativeElement,
        value: new Date(calculatedStartDate),
      });
      this.dateChange.emit({
        target: this.endDatePicker,
        targetElement: this.endDatePicker.nativeElement,
        value: new Date(),
        presetSelection: '12mtd',
      });
    } else {
      // Calculate time difference
      const calculateTime = value.split(' ')[0];
      const calculateUnit = value.split(' ')[1];
      const timeDiffObject = {};
      timeDiffObject[calculateUnit] = calculateTime;
      const calculatedStartDate = DateTime.now()
        .minus(timeDiffObject)
        .toLocaleString();

      // Set the date picker with the selected times
      this.start.setValue(new Date(calculatedStartDate));
      this.end.setValue(new Date());

      // Fire dateChange method to update the filters
      this.dateChange.emit({
        target: this.startDatePicker,
        targetElement: this.startDatePicker.nativeElement,
        value: new Date(calculatedStartDate),
      });
      this.dateChange.emit({
        target: this.endDatePicker,
        targetElement: this.endDatePicker.nativeElement,
        value: new Date(),
        presetSelection: value,
      });
    }
  }

  clearRange(ev) {
    this.start.setValue(null);
    this.end.setValue(null);
    this.filterForm.controls[this.groupKey].controls[
      this.field.varName
    ].setValue(null);
    this.dateChange.emit({
      target: this.startDatePicker,
      targetElement: this.startDatePicker.nativeElement,
      value: null,
    });
    this.dateChange.emit({
      target: this.endDatePicker,
      targetElement: this.endDatePicker.nativeElement,
      value: null,
    });
    this.presetRangeControl.setValue(null);
    this.clearFilter.emit(ev);
  }

  formattedRangeValue() {
    if (
      this.minField.value &&
      this.maxField.value &&
      typeof this.minField.value !== 'string' &&
      typeof this.maxField.value !== 'string'
    ) {
      return `$${this.minField.value} -> $${this.maxField.value}`;
    }

    if (this.minField.value && !this.maxField.value) {
      return `${this.minField.value} -> undefined`;
    } else if (!this.minField.value && this.maxField.value) {
      return `undefined -> ${this.maxField.value}`;
    } else if (this.minField.value && this.maxField.value) {
      return `${this.minField.value} -> ${this.maxField.value}`;
    }
    return null;
  }

  getBridgeOptions() {
    this.bmSvc.getPolicyBridgeOptions().subscribe(resp => {
      if (resp && resp['UnitPMBridges']) {
        this.field.options = resp['UnitPMBridges'];
      } else {
        this.field.options = ['noOptions'];
      }
      if (resp['UnitSettings']) {
        const { LaunchButtonLabel, LaunchLocation, ShowLaunchButton } =
          resp['UnitSettings'];
        this.bmSvc.updateLaunchButtonValues({
          LaunchButtonLabel,
          LaunchLocation,
          ShowLaunchButton,
        });
      }
      if (!this.field.options.length || this.field.options[0] === 'noOptions') {
        this.showBridgeIdSelect = false;
      } else {
        this.showBridgeIdSelect = true;
      }
      this.cstmFltrSrvc.reloadFilterValues(true);
    });
  }
}
